import browserInstance from './browser.mjs';
import { MALL_HOST, BASE_DOMAIN } from './shopeeDomains.mjs';
import { cdnFile } from './cdnURLs.mjs';
import { shopeeEnvironment } from './env.mjs';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * @module @mkt-common/shopee-common
 */
/**
 * Javascript assets load status
 * @returns {boolean}
 */
function scriptsLoadStatus() {
    if (browserInstance.shopeeApp) { // window.WebViewJavascriptBridge只在shopee app中初始化
        return !!(window.bridgeInit && window.connectWebViewJavascriptBridge && window.WebViewJavascriptBridge);
    }
    else {
        return !!(window.bridgeInit && window.connectWebViewJavascriptBridge);
    }
}
/**
 * set bridge call environment
 * @param isDev is develop environment
 */
function setBridgeEnv() {
    if (typeof window === 'undefined') {
        return;
    }
    window.skipSiteJSInit = true;
    window.MALL_HOST_ROOT_URL = MALL_HOST;
    if (shopeeEnvironment === 'test') {
        window.LOCALHOST = true;
    }
}
setBridgeEnv();
/**
 * jump
 * @param {string} path path string https://docs.google.com/document/d/1rb8in04yCbY0MOcjhTALLzbU52UqeNlIdOE4Xg5_opA/edit?ts=5a4eeec3#heading=h.xpoqc198uzl7
 */
function jump(path) {
    window.bridgeInit(() => {
        window.bridgeCallHandler('jump', {
            path,
        });
    });
}
/** 存储 navbar 右侧分享按钮点击触发事件 callback，用于绑定与解绑此事件 */
let shareEventCallback;
/**
 * APP built-in web page call bridge protocol method to initialize the top of the APP,
 * including setting the top of the title and share button
 * @param {string} title app top title
 * @param {string} text sharing text
 * @param {string} shareButtonIcon share button image
 * @param {number} isTransparent APP top bar is transparent or not (0 or 1)
 * @param {function} callback after evokes native app sharing will call callback
 */
function initAppHeaderShare(title, text, callback, shareButtonIcon = '5e98a3a576ac2a7d4ca2ba384f5897b2', isTransparent = 0) {
    // APP header 添加分享按钮
    const shareIconUrl = cdnFile(shareButtonIcon);
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('configurePage', {
                navbar: {
                    title,
                    navbarStyle: isTransparent,
                    isTransparent,
                    rightItemsConfig: {
                        items: [{
                                type: 'button',
                                key: 'share',
                                text,
                                iconUrl: shareIconUrl,
                            }],
                    },
                },
            });
            if (typeof shareEventCallback === 'function') {
                // 如果调用多次会出现重复注册的情况，因此这里先解绑
                window.bridgeUnRegisterHandler('onEventCallback', shareEventCallback);
            }
            // 点击 navbar 右侧 share 按钮的事件
            shareEventCallback = (e) => {
                if (e.key === 'share') {
                    // callback 内要业务方自己使用 shopee-share 进行分享
                    callback();
                }
            };
            window.bridgeRegisterHandler('onEventCallback', shareEventCallback);
        });
    }
}
/**
 * APP embedded web page call bridge protocol method to initialize APP's top title
 * @param {string} title app top title
 */
function initAppHeaderTitle(title) {
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('configurePage', {
                navbar: {
                    title,
                },
            });
        });
    }
}
/**
 * APP embedded web page call bridge protocol method to open another webview
 * When you open the home page, you need to call bridge to jump to the home
 * @param {string} url page link
 * @param {boolean} forceExternal force to use openExternalLink
 */
function shopeeOpen(url, forceExternal) {
    const reg = new RegExp(`^(https?://)?${BASE_DOMAIN}/?(\\?([^#]*))?(#(.*))?$`);
    if (reg.test(url) && !browserInstance.pc) {
        jump('home');
    }
    else if (browserInstance.shopeeApp && (browserInstance.android && !~url.indexOf(BASE_DOMAIN) || forceExternal)) {
        // 默认在Android中，外部链接使用openExternalLink，内部链接使用navigate
        openExternalLink(url);
    }
    else {
        window.BJUtil ? window.BJUtil.navigate(url, null, true) : (location.href = url);
    }
}
/**
 * Opens the given URL in the OS's environment (maybe a browser or other apps)
 * @param {string} url page link
 */
function openExternalLink(url) {
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('openExternalLink', { url });
        });
    }
}
/**
 * Add a mask to the top bar or not
 * @param {boolean} isDim should add a mask ( 0 or 1)
 * @param {string} color Hex string to represent dim color
 * @param {number} alpha 0 to 1.0
 */
function dimNavbar(isDim, color = '000000', alpha = 0.54) {
    const params = {
        isDim,
        color,
        alpha,
    };
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('dimNavbar', params);
        });
    }
}
/**
 * web page call bridge protocol method jump to login page
 * @param {string} type 1 for login tab, 0 for signUp tab
 * @param {string} redirectPath the path used to jump after the login is successful
 * @param {number} hidePopup 1 for hide popup, 0 for not hide popup
 * @param {boolean} refresh Control whether to actively refresh after logged in when inside the shopee app
 * @returns {promise}
 */
function login(type = 1, redirectPath = window.location.href, hidePopup = 1, refresh = true) {
    // pc mall
    if (browserInstance && browserInstance.pcmall) {
        parent.postMessage('{"cmd": "showLoginPopup"}', '*');
        return Promise.resolve();
    }
    // for mweb, 登录成功后返回强制刷新
    if (browserInstance && browserInstance.mweb && window) {
        window.onpageshow = (event) => {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }
    // fix android can not back then login
    const url = { paths: [{ webNav: { url: redirectPath } }] };
    const path = encodeURIComponent(window.btoa(JSON.stringify(url)));
    // mweb and app
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('login', { redirectTab: type, redirectPath: `home?navRoute=${path}`, hidePopup }, (data) => {
                    if (browserInstance.shopeeApp) {
                        // if refresh is true then reload page
                        if (refresh) {
                            const appVersion = window.getAppVersion && window.getAppVersion();
                            if (appVersion >= 25400) {
                                // fix Shopee App V2.54 and later versions iOS WKWeview not reloaded after native `history.back()`
                                window.location.reload();
                            }
                            else {
                                window.location.href = window.MALL_HOST_ROOT_URL + '/bridge_cmd/?cmd=load_then_back';
                            }
                        }
                    }
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * Check user has logged in or not
 * see: https://confluence.shopee.io/display/SPAT/isLoggedIn
 * @returns {promise}
 */
function isLoggedIn() {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('isLoggedIn', {}, (res) => {
                    if (!res || res.error !== 0) {
                        resolve(false);
                    }
                    else {
                        resolve(res && res.data && res.data.isLoggedIn);
                    }
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * get App info such as device id
 * @returns {promise}
 */
function getAppInfo() {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('getAppInfo', {}, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * ask app to dismiss the webview
 * @param {number} count The number of pages to pop
 * @param {string} data The next webview in the navigation stack that will appear will receive a call viewDidReappear with this data as parameter. This field is ignored if urlQueryString is set
 * @param {string} urlQueryString This query string will be appended to the url of the previous webview. This is used when we want to reload the previous webview with new parameters
 * @returns {promise}
 */
function closeWebView(count = 1, data, urlQueryString) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('popWebView', { count, data, urlQueryString }, (res) => {
                    resolve(res);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * Command to check whether a list of permissions is currently granted or not
 * @param {'contact'|'sms'|'calendar'|'camera'|'location'|'record_audio'|'storage'|'push_notification'} permission  permission
 * @returns {promise}
 */
function checkAppPermission(permission) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            if (window.getAppVersion) {
                const version = window.getAppVersion();
                if (browserInstance.ios && version < 22600) {
                    return reject(`ios version:${version} not support checkAppPermission cmd`);
                }
                if (browserInstance.android && version < 22506) {
                    return reject(`android version:${version} not support checkAppPermission cmd`);
                }
            }
            window.bridgeInit(() => {
                // fix can not invoke callback for ios
                // auto reject when timeout 10s
                const timer = setTimeout(() => {
                    return reject('timeout call checkAppPermission');
                }, 10000);
                window.bridgeCallHandler('checkAppPermission', { permissionList: [permission] }, function (data) {
                    clearTimeout(timer);
                    if (data && data.resultList) {
                        try {
                            if (typeof data.resultList === 'string') {
                                resolve(JSON.parse(data.resultList)[0]);
                            }
                            else {
                                resolve(data.resultList[0]);
                            }
                        }
                        catch (e) {
                            resolve(false);
                        }
                    }
                    else {
                        reject(data);
                    }
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * Command to start requesting permission for both platform.
 * @param {'contact'|'sms'|'calendar'|'camera'|'location'|'record_audio'|'storage'|'push_notification'} permission  permission
 * @param {string} popupText  text for popup
 * @returns {promise} Please note that success or fail here only tell that the permissions requesting process is done. will not tell the permissions are granted or not.
 */
function requestAppPermission(permission, popupText) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('requestAppPermission', { permissionList: [permission], popupText }, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * disable reload
 */
function disableReload() {
    configurePage({ disableReload: 1 });
}
/**
 * enable reload
 */
function enableReload() {
    configurePage({ disableReload: 0 });
}
function disableBounce() {
    configurePage({ disableBounce: 1 });
}
function enableBounce() {
    configurePage({ disableBounce: 0 });
}
/**
 * disable rotate
 */
function disableRotate() {
    configurePage({ enableRotation: 0 });
}
/**
 * enable rotate
 */
function enableRotate() {
    configurePage({ enableRotation: 1 });
}
function enableAutoPlayMedia() {
    // works on >= 2.23.7 IOS
    configurePage({ autoPlayMedia: 1 });
}
function disableAutoPlayMedia() {
    // works on >= 2.23.7 IOS
    configurePage({ autoPlayMedia: 0 });
}
/**
 * config page and navbar
 * @params {pageConfig} IConfigurePage | ConfigurePageConfig
 * 为了兼容之前只有 config 参数的格式, 需要判断传进来的参数是哪种类型的
 */
function checkParamsIsIConfigurePage(pageConfig) {
    const { config, navbar } = pageConfig;
    return !!(config || navbar);
}
function configurePage(pageConfig) {
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            if (checkParamsIsIConfigurePage(pageConfig)) {
                const { config, navbar } = pageConfig;
                window.bridgeCallHandler('configurePage', { config, navbar });
            }
            else {
                window.bridgeCallHandler('configurePage', { config: pageConfig });
            }
        });
    }
}
function adjustWebViewInsets({ customTopInset, customLeftInset, customRightInset, customBottomInset }) {
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('adjustWebViewInsets', {
                customTopInset,
                customLeftInset,
                customRightInset,
                customBottomInset,
            });
        });
    }
}
/**
 * call to report BI data
 * Please use `@mkt-common/shopee-track` instead.
 * @param {Array} eventDataList
 */
function trackBIShopeeApp(eventDataList) {
    if (scriptsLoadStatus()) {
        window.bridgeInit(() => {
            window.bridgeCallHandler('trackBIEvent', {
                events: JSON.stringify(eventDataList),
            });
        });
    }
}
/**
 * call to show app toast
 * @param {toast}
 */
function showToast(toast) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeCallHandler('showToast', { toast }, (res) => {
                // ios is status:1
                // android is error:0
                if (res && (res.error === 0 || res.status === 1)) {
                    // success
                    resolve();
                }
                else {
                    reject(new Error(res.errorMessage));
                }
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * sync contact data
 */
function syncContactData() {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            if (window.getAppVersion && window.getAppVersion() < 22600) {
                return reject(`version:${window.getAppVersion()} not support this cmd`);
            }
            window.bridgeInit(() => {
                window.bridgeCallHandler('startDataCollection', { contentTypes: ['contact'] });
                window.bridgeRegisterHandler('COLLECT_DATA_EVENT', (data) => {
                    let syncResult = data && data.error === 0;
                    if (!syncResult) {
                        // 发现在 android 中返回的数据中不是error字段，而是status字段
                        syncResult = data && data.status === 0;
                    }
                    resolve({ syncResult, data });
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * 获取通讯录数据
 * https://confluence.shopee.io/display/SPAT/Contact+Manager
 * @param {ContactListParams} params 参数
 */
function getContactList(params) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            if (window.getAppVersion && window.getAppVersion() < 25813) {
                return reject(`version:${window.getAppVersion()} not support this cmd`);
            }
            window.bridgeInit(() => {
                window.bridgeCallHandler('getContactList', params, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * get location
 */
function getLocation() {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            if (window.getAppVersion && window.getAppVersion() < 22600) {
                return reject(`version:${window.getAppVersion()} not support this cmd`);
            }
            window.bridgeInit(() => {
                window.bridgeCallHandler('getLocation', null, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * app 内 H5页面跳转到客户端页面（例如：ar catch 点击play跳到游戏页面，live quiz 是跳到直播页面）
 * @param {string} path page path（和app协商定义的路径）
 * @param {boolean} closeWebview 跳转到app以后是否关闭 webview, 关闭后无法再返回h5页面
 */
function navigateAppPath(path, closeWebview = true) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('navigateAppPath', { path, popSelf: closeWebview }, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * https://confluence.shopee.io/display/SPAT/Navigate+AppRL
 * In the app, you can jump to the corresponding rn page according to appRl,
 * for example, can jump to the PRS RN home page by 'rn/@shopee-rn/web-redeem-rn/HOME'
 * @param {string} apprl
 * @param {object} params page navigation parameter, optional
 */
function navigateAppRL(apprl, params) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('navigateAppRL', {
                    apprl,
                    params: params || {},
                });
                resolve(undefined);
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * https://confluence.shopee.io/pages/viewpage.action?pageId=289251078
 * @param {Function} showCallback Web bridge event triggered when the webview running the bridge re-appears on the screen.
 * @param {Function} hideCallback (optional) Web bridge event triggered when the webview running the bridge disappears from the screen.
 */
function addWebViewVisibilityListener(showCallback, hideCallback) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                if (typeof showCallback === 'function') {
                    window.bridgeRegisterHandler('viewWillReappear', showCallback);
                }
                if (typeof hideCallback === 'function') {
                    window.bridgeRegisterHandler('viewDidDisappear', hideCallback);
                }
                resolve(undefined);
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * 判断 google play 是否可用
 */
function isMapAvailable() {
    if (browserInstance.android) {
        return new Promise((resolve, reject) => {
            if (scriptsLoadStatus()) {
                window.bridgeInit(() => {
                    window.bridgeCallHandler('isMapAvailable', {}, (data) => {
                        resolve(!!data.status);
                    });
                });
            }
            else {
                reject('Bridge Js files load failed');
            }
        });
    }
    else {
        return Promise.resolve(true);
    }
}
/**
 * click back in header, and to do something
 * @param callBack
 * @param [options] - { onlyOnce?: boolean } 是否只需要注册执行一次
 */
function didTapBack(callBack, options) {
    const id = `didTapBack_${Date.now()}`;
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('didTapBack');
                window.bridgeRegisterHandler('didTapBack', (data) => {
                    callBack(data, id);
                    if (options && options.onlyOnce) {
                        window.bridgeUnRegisterHandler('didTapBack', id);
                    }
                    resolve();
                }, id);
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * Check whether the current version of APP supports successful sharing feedback
 * @returns {boolean}
 */
function isShareCallbackSupported() {
    if (window.isIOS()) {
        return window.getAppVersion() >= 22204;
    }
    else if (window.isAndroid()) {
        return window.getAppVersion() >= 22201;
    }
    return false;
}
/**
 * Evokes native app sharing function
 * @deprecated please use new share bridge api,detail see shopee-share package
 * @param {string} url sharing url
 * @param {string} text sharing text
 * @param {string} icon sharing image link for instagram
 * @param {function} callback after evokes native app sharing will call callback
 */
function shopeeShare(url, text, icon, callback) {
    window.bridgeInit(() => {
        share(url, text, icon, callback);
    });
}
/**
 * Inline web page evokes native app sharing
 * @deprecated please use new share bridge api,detail see shopee-share package
 * @param {string} url sharing url
 * @param {string} text sharing text
 * @param {string} icon sharing image link for instagram
 * @param {function} callback after evokes native app sharing will call callback
 */
function share(url, text, icon, callback) {
    const textAndUrl = `${text} ${url}`;
    window.bridgeCallHandler('share', {
        shareType: 4,
        url,
        sharingText: {
            copy_info: textAndUrl,
            copy_link: url,
            sms: textAndUrl,
            instagram: text,
            // IOS APP 在2.26中已修复，iOS 升级率比较高
            // 旧版本不用管
            whatsapp: textAndUrl,
            twitter: textAndUrl,
            line: textAndUrl,
            pinterest: textAndUrl,
            beetalk: textAndUrl,
            buzz: textAndUrl,
            email_body: textAndUrl,
            email_subject: text,
        },
        genericImageUrl: icon,
    }, (data) => {
        callback && callback(data, 1);
    });
    // 如果是低版本，app 没有实现调用 callback
    // 我们自己主动吊起 callback
    if (!isShareCallbackSupported()) {
        callback && callback(null, 2);
    }
}
/**
 * save media(image/video) to camera roll
 * @param {string} url path to local media file
 * @param {number} type image: 0, video: 1
 */
function saveMediaToDeviceAlbum(url, type) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('saveMediaToDeviceAlbum', { url, type }, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * The bridge accepts the text to be encrypted and the encryption method and returns the encrypted string along with the framework version as the result.
 * (注意：由于 app 加密时没有加锁，所以同时调用2次及以上时，可能会出现问题；需等一个调用执行完毕后，再进行下一个加密)
 * app version: abover ANDROID: 25207, IOS: 25214
 * @param {string} text the text to be encrypted
 * @param {1 | 2 | 3 | 4 | 5 | 6} method (0: AEGIS_B64, 1: AEGIS_AES_CBC, 2: AEGIS_AES_CBC_B64, 3: AEGIS_IV_AES_CBC_B64, 4: AEGIS_MD5, 5: AEGIS_SHA256, 6: AEGIS_SHA512)
 */
function transformText(text, method) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('transformText', { text, method }, (data) => {
                    resolve(data);
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * Add a calendar event with alarm to Calendar app (for iOS) or create a local push notification that will come at a specific time (for Android)
 * Doc: https://confluence.shopee.io/display/SPAT/Reminder+Bridge#ReminderBridge-addReminder
 *
 * **On IOS, please use `checkAppPermission` to check `calendar` permission first.**
 *
 * @param {Object} params
 * @param {string} params.title - The title of reminder event.
 * @param {string} params.androidMsg - The message of the event on **Android**
 * @param {string} params.androidRedirectURI - The redirect path of the event on **Android**. **This need to be an [App Route](https://confluence.shopee.io/display/SPAT/App+Path+and+App+Route)**
 * @param {string} params.iOSMsg - The message of the event on **IOS**
 * @param {number} params.startDate - The timestamp of start date of the event (**in seconds**).
 * @param {number} [params.endDate=params.startDate] - The timestamp of end date of the event (**in seconds**).
 * @param {number} [params.reminderOffset=0] - The offset time of the reminder to the startDate of the event (**in minutes**).
 */
function addReminder(params) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeCallHandler('addReminder', {
                title: params.title,
                startDate: params.startDate,
                endDate: typeof params.endDate !== 'undefined' ? params.endDate : params.startDate,
                reminderOffset: typeof params.reminderOffset !== 'undefined' ? params.reminderOffset : 0,
                android: {
                    message: params.androidMsg,
                    redirect: params.androidRedirectURI,
                },
                iOS: {
                    notes: params.iOSMsg,
                },
            }, (data) => {
                if (data.error === 0 && data.data && data.data.reminderId) {
                    resolve(data.data.reminderId);
                }
                else {
                    reject(data.errorMsg);
                }
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * ios < V2.53.XX, set calendar event
 *
 * ios > V2.53.XX and android, create a local push notification
 *
 * The differences from `addReminder` are as follows:
 * 1. auto use `checkAppPermission` to check `calendar` permission and use `requestAppPermission` to apply calendar permission
 * 2. auto translate http url to AppRoute
 * 3. toast remind result
 */
const addReminderV2 = (params, toastInfo) => {
    return new Promise((resolve, reject) => {
        const appVersion = window.getAppVersion && window.getAppVersion();
        const getRedirect = (url) => {
            if (!/^https?\:/.test(url)) {
                // 非web url 直接返回，调用方需要确保是App Route
                return url;
            }
            // web url时，自动处理好App Route
            const appRouteUrl = { paths: [{ webNav: { url } }] };
            return 'home?navRoute=' + encodeURIComponent(window.btoa(JSON.stringify(appRouteUrl)));
        };
        const commonParams = {
            title: params.title,
            startDate: params.startDate,
            endDate: typeof params.endDate !== 'undefined' ? params.endDate : params.startDate,
            reminderOffset: typeof params.reminderOffset !== 'undefined' ? params.reminderOffset : 0,
            android: {
                message: params.androidMsg,
                redirect: getRedirect(params.androidRedirectURI),
            },
        };
        const resCallback = (data) => {
            let toastText;
            let iconType;
            if (data.error === 0 && data.data && data.data.reminderId) {
                toastText = toastInfo && toastInfo.successToastText;
                iconType = 'success';
            }
            else {
                toastText = (toastInfo && toastInfo.failureToastText) || data.errorMessage;
                iconType = 'failure';
            }
            let p = Promise.resolve(false);
            if (toastInfo && toastInfo.showToast && toastText) {
                // toast success
                p = showToast({
                    iconType,
                    message: toastText,
                }).then(() => true).catch(() => false);
            }
            p.then((shownToast) => {
                resolve({
                    error: data.error,
                    reminderId: data.data && data.data.reminderId,
                    impressionToast: shownToast,
                });
            }).catch(reject);
        };
        const setCalendar = () => {
            // 默认将 url 拼到 calendar notes 里
            const appendURIInCalendarNote = typeof params.appendURIInCalendarNote === 'undefined' ? true : params.appendURIInCalendarNote;
            window.bridgeCallHandler('addReminder', Object.assign(Object.assign({}, commonParams), { iOS: {
                    eventType: 0,
                    message: params.iOSMsg,
                    notes: appendURIInCalendarNote ? params.iOSMsg + ' ' + params.iOSRedirectURI : params.iOSMsg,
                    redirect: getRedirect(params.iOSRedirectURI),
                } }), resCallback);
        };
        if (window.bridgeInit) {
            window.bridgeInit(() => {
                if (browserInstance.android || (browserInstance.ios && appVersion > 25300)) {
                    // android或者ios，且大于2.53，直接发PN
                    window.bridgeCallHandler('addReminder', Object.assign(Object.assign({}, commonParams), { iOS: {
                            eventType: 1,
                            message: params.iOSMsg,
                            notes: params.iOSMsg,
                            redirect: getRedirect(params.iOSRedirectURI),
                        } }), resCallback);
                }
                else {
                    // ios && 小于等于2.53，设置calendar，需要先检查calendar权限
                    checkAppPermission('calendar').then((hasPermission) => {
                        if (!hasPermission) {
                            // 没有calendar权限，则先申请权限
                            requestAppPermission('calendar', params.requestCalendarPermissionText).then((status) => {
                                if (!status) {
                                    return reject(new Error('request calender permission failed'));
                                }
                                setCalendar();
                            }).catch(reject);
                        }
                        else {
                            setCalendar();
                        }
                    }).catch(reject);
                }
            });
        }
    });
};
/**
 * 判断是否在 WKWebview 上，仅在 ios 上生效
 * bridge returns { status: 0 | 1 } (0: not in WKWebview, 1: is in WKWebview)
 */
function isInWKWebView() {
    if (browserInstance.ios) {
        return new Promise((resolve, reject) => {
            if (scriptsLoadStatus()) {
                window.bridgeInit(() => {
                    window.bridgeCallHandler('isInWKWebView', {}, (res) => {
                        resolve(!!res.status);
                    });
                });
            }
            else {
                reject('Bridge Js files load failed');
            }
        });
    }
    else {
        return Promise.reject(false);
    }
}
/**
 * 仅支持 ios 12及以上，慎用！！！ios 默认 webview 即将切换成 WKWebview
 * IOS 上强制切换成 WKWebview，会强制刷新一次
 * 只有 ios 12及以上能使用，ios 12 以下会出现切换不了，一直 reload 的情况
 * (PS: 在测试 app 的 WebView Type 上必须选择 Default，因为这个权重大于此 bridge)
 */
function useWKWebview() {
    return __awaiter(this, void 0, void 0, function* () {
        if (browserInstance.ios && scriptsLoadStatus()) {
            const res = yield isInWKWebView();
            if (!res) {
                window.bridgeInit(() => {
                    window.bridgeCallHandler('navigate', { url: window.location.href, popSelf: 1, useWKWebView: 1 });
                });
            }
        }
    });
}
/**
 * 监听 shake 事件，可用于代替原生的 `devicemotion` 事件。[Shake Bridge 文档](https://confluence.shopee.io/display/SPAT/Shake+Bridge)
 *
 * @export
 * @param {Function} callback - shake 触发后的回调
 * @returns {Promise<boolean>} - 是否监听成功
 */
function addShakeEventListener(callback) {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('startShakeDetection', {}, (data) => {
                    if (data.error === 0) {
                        window.bridgeRegisterHandler('SHAKE_EVENT', () => {
                            callback && callback();
                        });
                        resolve(true);
                    }
                    else {
                        reject('failed to call `startShakeDetection` bridge');
                    }
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * 取消监听 shake 事件，配合 `addShakeEventListener` 方法使用。[Shake Bridge 文档](https://confluence.shopee.io/display/SPAT/Shake+Bridge)
 *
 * @export
 * @returns {Promise<boolean>} - 是否成功
 */
function stopListenShakeEvent() {
    return new Promise((resolve, reject) => {
        if (scriptsLoadStatus()) {
            window.bridgeInit(() => {
                window.bridgeCallHandler('stopShakeDetection', {}, (data) => {
                    if (data.error === 0) {
                        resolve(true);
                    }
                    else {
                        reject('failed to call `stopShakeDetection` bridge');
                    }
                });
            });
        }
        else {
            reject('Bridge Js files load failed');
        }
    });
}
/**
 * -----------------
 * |  一些常用配置    |
 * -----------------
 */
/**
 * 去除 ios 全面屏底下的白边
 */
function removeBottomInset() {
    adjustWebViewInsets({ customBottomInset: 0 });
}
/**
 * @description 非全屏
 * 初始化 app webview 配置
 */
function configurePageWithoutFullScreen() {
    const { shopeeApp, ios } = browserInstance;
    if (shopeeApp) {
        if (ios) {
            configurePage({
                autoPlayMedia: 1,
                disableReload: 1,
                disableBounce: 1,
            });
        }
        else {
            configurePage({ disableReload: 1 });
        }
    }
}
/**
 * @description 全屏
 * 初始化 app webview 配置
 */
function configurePageWithFullScreen() {
    const { shopeeApp, ios } = browserInstance;
    if (shopeeApp) {
        if (ios) {
            configurePage({
                config: {
                    autoPlayMedia: 1,
                    disableReload: 1,
                    disableBounce: 1,
                },
                navbar: {
                    isTransparent: 1,
                    hideBackButton: 1,
                },
            });
            removeBottomInset();
        }
        else {
            configurePage({
                config: {
                    disableReload: 1,
                },
                navbar: {
                    isTransparent: 1,
                    hideBackButton: 1,
                },
            });
        }
    }
}

export { addReminder, addReminderV2, addShakeEventListener, addWebViewVisibilityListener, adjustWebViewInsets, checkAppPermission, closeWebView, configurePage, configurePageWithFullScreen, configurePageWithoutFullScreen, didTapBack, dimNavbar, disableAutoPlayMedia, disableBounce, disableReload, disableRotate, enableAutoPlayMedia, enableBounce, enableReload, enableRotate, getAppInfo, getContactList, getLocation, initAppHeaderShare, initAppHeaderTitle, isInWKWebView, isLoggedIn, isMapAvailable, login, navigateAppPath, navigateAppRL, removeBottomInset, requestAppPermission, saveMediaToDeviceAlbum, share, shopeeOpen, shopeeShare, showToast, stopListenShakeEvent, syncContactData, trackBIShopeeApp, transformText, useWKWebview };
